import audioPlaceholder from '../../../assets/images/section-menu/audio-placeholder.png';

/**
 * Convert sections from backend format to timeline items and assets
 * @param {Array} sections - Sections from backend API
 * @returns {Object} - { assets: [], timelineItems: [] }
 */
export function convertSectionsToTimeline(sections) {
  const assets = [];
  const timelineItems = [];
  let startTime = 0;

  sections.forEach((section, index) => {
    // Handle question sections – include in timeline with fixed preview duration
    if (section.type === 'question') {
      const assetId = `section-${section.id}`;
      const duration = 5; // Preview duration for questions

      assets.push({
        id: assetId,
        type: 'question',
        source_type: 'project',
        name: section.title || `Question ${index + 1}`,
        text: section.Question?.text || '',
        isAnswerRequired: section.Question?.isAnswerRequired || false,
        options: section.Question?.options || { type: 'Single Text Field', options: [] },
        duration,
        metadata: {
          sectionId: section.id,
          questionId: section.Question?.id,
          questionAnswers: section.Question?.Answers || [], // Include all answers
          userId: section.userId,
          creatorPhotoPath: section.creator?.photoPath,
          creatorName: section.creator?.name,
          isSaved: !!section.SavedSection, // Store saved status in asset metadata
        },
      });

      timelineItems.push({
        id: `timeline-${assetId}`,
        asset_id: assetId,
        duration,
        start_time: startTime,
        order: timelineItems.length,
        settings: {},
        metadata: {
          sectionId: section.id,
        },
      });

      startTime += duration;
      return;
    }

    // Handle text (note) sections
    if (section.type === 'note') {
      const assetId = `section-${section.id}`;
      const duration = 5;

      assets.push({
        id: assetId,
        type: 'text',
        source_type: 'project',
        name: section.title || `Text ${index + 1}`,
        text: section.text || '',
        duration,
        metadata: {
          sectionId: section.id,
          userId: section.userId,
          creatorPhotoPath: section.creator?.photoPath,
          creatorName: section.creator?.name,
          isSaved: !!section.SavedSection, // Store saved status in asset metadata
        },
      });

      timelineItems.push({
        id: `timeline-${assetId}`,
        asset_id: assetId,
        duration,
        start_time: startTime,
        order: timelineItems.length,
        settings: {},
        metadata: { sectionId: section.id },
      });

      startTime += duration;
      return;
    }

    // Handle placeholder sections (used in templates)
    if (section.type === 'placeholder') {
      const assetId = `section-${section.id}`;
      const duration = 5;

      assets.push({
        id: assetId,
        type: 'placeholder',
        source_type: 'placeholder',
        name: section.title || `Placeholder ${index + 1}`,
        url: null,
        thumbnail_url: null,
        duration,
        metadata: {
          sectionId: section.id,
          description: section.text || '',
        },
      });

      timelineItems.push({
        id: `timeline-${assetId}`,
        asset_id: assetId,
        duration,
        start_time: startTime,
        order: timelineItems.length,
        settings: {},
        metadata: { sectionId: section.id },
      });

      startTime += duration;
      return;
    }

    // Handle assigned-section sections
    if (section.type === 'assigned-section') {
      const assetId = `section-${section.id}`;
      const duration = 5; // Placeholder duration for assigned sections

      const assignedName = section.assigned?.name || section.assignedEmail || 'User';

      assets.push({
        id: assetId,
        type: 'assigned-section',
        source_type: 'project',
        name: `Assigned to ${assignedName}`,
        duration,
        metadata: {
          sectionId: section.id,
          assignedUserId: section.assignedUserId,
          assignedEmail: section.assignedEmail,
          assignedPhotoPath: section.assigned?.photoPath,
          assignedName,
          isSaved: !!section.SavedSection, // Store saved status in asset metadata
        },
      });

      timelineItems.push({
        id: `timeline-${assetId}`,
        asset_id: assetId,
        duration,
        start_time: startTime,
        order: timelineItems.length,
        settings: {},
        metadata: {
          sectionId: section.id,
          assignedUserId: section.assignedUserId,
          assignedEmail: section.assignedEmail,
        },
      });

      startTime += duration;
      return;
    }

    let assetType = section.type;
    let assetUrl = section.signedS3Url;
    const thumbnailUrl = section.signedThumbnailS3FileKey || null;

    // Handle attachments (images/documents)
    if (section.SectionAttachments && section.SectionAttachments.length > 0) {
      section.SectionAttachments.forEach((attachment) => {
        if (attachment.type === 'image') {
          assetType = 'image';
          assetUrl = attachment.signedS3Url;
        } else if (attachment.type === 'document') {
          assetType = 'document';
          assetUrl = attachment.signedS3Url;
        }

        // Create asset for attachment
        const assetId = `section-${section.id}-attachment-${attachment.id}`;
        const attachmentThumbnail = attachment.signedThumbnailS3FileKey || (attachment.type === 'image' ? attachment.signedS3Url : null);
        // Prefer section title for display, but keep original filename for metadata/icon logic
        const attachmentFileName = attachment.fileName || attachment.name || '';
        const assetName = section.title || attachmentFileName || `Attachment ${index + 1}`;
        const assetMimeType = attachment.mimeType || attachment.mime_type || null;
        assets.push({
          id: assetId,
          type: assetType,
          source_type: 'project',
          name: assetName,
          url: assetUrl,
          thumbnail_url: attachmentThumbnail,
          mime_type: assetMimeType,
          duration: section.duration || 5,
          metadata: {
            sectionId: section.id,
            attachmentId: attachment.id,
            userId: section.userId,
            creatorPhotoPath: section.creator?.photoPath,
            creatorName: section.creator?.name,
            attachmentFileName,
            attachmentMimeType: assetMimeType,
            // Store attachment name and description for editing
            attachmentName: attachment.fileName || attachment.name || '',
            attachmentDescription: attachment.description || '',
            isSaved: !!section.SavedSection, // Store saved status in asset metadata
          },
        });

        // Create timeline item for attachment
        timelineItems.push({
          id: `timeline-${assetId}`,
          asset_id: assetId,
          duration: section.duration || 5,
          start_time: startTime,
          order: timelineItems.length,
          settings: {
            trim_start: 0,
            trim_end: 0,
          },
          metadata: {
            sectionId: section.id,
            attachmentId: attachment.id,
          },
        });

        startTime += section.duration || 5;
      });
    } else if (section.type === 'video' || section.type === 'audio' || (section.signedS3Url && !section.SectionAttachments)) {
      // Create asset for video/audio section
      // Also handle sections with signedS3Url but no explicit type (fallback)
      const assetId = `section-${section.id}`;
      // Determine asset type: preserve audio type, default to video for others
      // Override assetType that was set from section.type above
      if (section.type === 'audio') {
        assetType = 'audio';
      } else if (section.type !== 'video') {
        // For sections without explicit type but with signedS3Url, default to video
        assetType = 'video';
      }
      // For audio sections, use audio placeholder as thumbnail; for video, use actual thumbnail
      const assetThumbnail = section.type === 'audio' 
        ? audioPlaceholder
        : thumbnailUrl;
      
      // Read trim settings from section.trimSettings
      const { trimSettings } = section;
      const trimStart = trimSettings?.trimStart ?? 0;
      const trimEnd = trimSettings?.trimEnd ?? 0;
      // Use originalDuration from trimSettings if available, otherwise use section.duration
      // For uploaded videos: section.duration is original (client-side trim)
      // For library-picked videos: section.duration might be trimmed (server-side trim)
      const originalDuration = trimSettings?.originalDuration ?? (section.duration || 0);
      // Calculate trimmed duration: originalDuration - trimStart - trimEnd
      const expectedTrimmedDuration = trimSettings
        ? originalDuration - trimStart - trimEnd
        : (section.duration || 0);
      // If backend already trimmed the file, avoid applying trim again in playback
      // Treat any section with trimSettings and shorter duration as server-trimmed
      const isServerTrimmed =
        !!trimSettings &&
        (trimStart > 0 || trimEnd > 0) &&
        typeof section.duration === 'number' &&
        section.duration < originalDuration;
      const effectiveTrimStart = isServerTrimmed ? 0 : trimStart;
      const effectiveTrimEnd = isServerTrimmed ? 0 : trimEnd;
      const assetDuration = isServerTrimmed
        ? (section.duration || 0)
        : originalDuration;
      const trimmedDuration = isServerTrimmed
        ? (section.duration || 0)
        : expectedTrimmedDuration;
      
      assets.push({
        id: assetId,
        type: assetType,
        source_type: 'project',
        name: section.title || `Section ${index + 1}`,
        url: assetUrl,
        thumbnail_url: assetThumbnail,
        duration: assetDuration, // Store playback duration for asset
        metadata: {
          sectionId: section.id,
          userId: section.userId,
          creatorPhotoPath: section.creator?.photoPath,
          creatorName: section.creator?.name,
          isSaved: !!section.SavedSection, // Store saved status in asset metadata
        },
      });

      // Create timeline item
      timelineItems.push({
        id: `timeline-${assetId}`,
        asset_id: assetId,
        duration: trimmedDuration,
        start_time: startTime,
        order: timelineItems.length,
        settings: {
          trim_start: effectiveTrimStart,
          trim_end: effectiveTrimEnd,
        },
        metadata: {
          sectionId: section.id,
        },
      });

      startTime += trimmedDuration;
    }
  });

  return { assets, timelineItems };
}
